// Mirror-federation retrieval race (szzt-spec.md §13, §13.1).
//
// `hints` (§4.1) is a list of site-level mirror hostnames. A bare host means RASL — retrieve via
// `https://<host>/.well-known/rasl/<cid>`; a host prefixed `ipfs:` denotes an IPFS gateway —
// retrieve via the standard IPFS convention, either `<cid>.ipfs.<host>` subdomain or `/ipfs/<cid>`
// path. Retrieval races every candidate mirror URL in parallel and stops at the FIRST HASH-VERIFIED
// success; the owner's PDS is the authoritative backstop, tried when every mirror fails. A mirror
// carries NO trust: every byte pulled from it is re-hashed against the CID (the same recompute path
// `verify-cache.ts` uses) before it is accepted, so a hostile, misconfigured, or lapsed mirror can
// only fail verification and be skipped — it can never serve bad bytes, and it never breaks the
// site (fail-through to the next candidate, then to the PDS, not fail-closed on one bad mirror).
//
// All network access lives behind ONE injected seam, `UrlFetcher`; this module ships zero untested
// network code, mirroring how the rest of the package keeps load-bearing logic pure.

import { recomputeBlobCid } from "@szzt/records";
import { isPublicHttpsUrl } from "./ssrf.ts";
import type { Fetcher } from "./types.ts";

/** Prefix marking a `hints` entry as an IPFS gateway host rather than a bare RASL host (§13.1). */
export const IPFS_HINT_PREFIX = "ipfs:";

/**
 * The injected network seam for mirror retrieval: fetch a candidate mirror URL's bytes. A real
 * deployment supplies a `fetch()`-based implementation subject to the outbound constraints of §9.1
 * and to CORS (§13.1) — this package never implements it, only depends on it. A fetch that fails
 * (unreachable host, CORS refusal, non-200 status) MUST reject; that candidate is then simply
 * skipped by the race in {@link MirrorFetcher}.
 */
export interface UrlFetcher {
  fetchUrl(url: string): Promise<Uint8Array>;
}

/** A parsed `hints` (§4.1) entry: a bare RASL mirror host, or an `ipfs:`-prefixed gateway host. */
export type MirrorHint = { kind: "rasl"; host: string } | { kind: "ipfs"; host: string };

/**
 * Parse one `hints` entry (§13.1). Trims surrounding whitespace; an empty entry is `null`. An entry
 * starting with {@link IPFS_HINT_PREFIX} is an IPFS gateway host with the prefix stripped (an empty
 * remaining host is also `null`); anything else is a bare RASL host.
 */
export function parseHint(hint: string): MirrorHint | null {
  const trimmed = hint.trim();
  if (trimmed === "") return null;
  if (trimmed.startsWith(IPFS_HINT_PREFIX)) {
    const host = trimmed.slice(IPFS_HINT_PREFIX.length);
    if (host === "") return null;
    return { kind: "ipfs", host };
  }
  return { kind: "rasl", host: trimmed };
}

/** The RASL well-known URL for a content CID on a bare-host mirror (§9.2, §13). */
export function raslWellKnownUrl(host: string, cid: string): string {
  return `https://${host}/.well-known/rasl/${cid}`;
}

/** The IPFS subdomain-gateway URL for a content CID (§13.1). */
export function ipfsSubdomainUrl(host: string, cid: string): string {
  return `https://${cid}.ipfs.${host}/`;
}

/** The IPFS path-gateway URL for a content CID (§13.1). */
export function ipfsPathUrl(host: string, cid: string): string {
  return `https://${host}/ipfs/${cid}`;
}

/** Every candidate URL a single parsed hint offers for a content CID: one for `rasl`, both IPFS forms for `ipfs`. */
export function hintCandidateUrls(hint: MirrorHint, cid: string): string[] {
  if (hint.kind === "rasl") return [raslWellKnownUrl(hint.host, cid)];
  return [ipfsSubdomainUrl(hint.host, cid), ipfsPathUrl(hint.host, cid)];
}

/**
 * All candidate mirror URLs for a content CID across a `hints` list (§13, §13.1). Parses each hint
 * (dropping unparseable entries), expands it to its candidate URL(s), and keeps only URLs that pass
 * {@link isPublicHttpsUrl} (§9.1) — a private/loopback/malformed hint is silently skipped. Order is
 * preserved and exact-duplicate URLs are removed.
 */
export function candidateUrls(hints: readonly string[], cid: string): string[] {
  const seen = new Set<string>();
  const urls: string[] = [];
  for (const raw of hints) {
    const hint = parseHint(raw);
    if (hint === null) continue;
    for (const url of hintCandidateUrls(hint, cid)) {
      if (!isPublicHttpsUrl(url)) continue;
      if (seen.has(url)) continue;
      seen.add(url);
      urls.push(url);
    }
  }
  return urls;
}

/** Constructor options for {@link MirrorFetcher}. */
export interface MirrorFetcherOptions {
  /** The owner's PDS, tried as the authoritative backstop when no mirror serves verified bytes. */
  pds: Fetcher;
  /** The injected network seam for mirror URLs. */
  urlFetcher: UrlFetcher;
  /** The site's `hints` (§4.1); may be empty. */
  hints: readonly string[];
}

/**
 * A {@link Fetcher} that races a site's `hints` mirrors before falling back to the owner's PDS
 * (§13). Every mirror candidate is re-verified against the requested CID before being accepted, so
 * this class carries no trust of its own: a wrong, corrupt, or unreachable mirror is indistinguishable
 * from an absent one, and retrieval falls through to the next candidate and ultimately the PDS.
 */
export class MirrorFetcher implements Fetcher {
  private readonly pds: Fetcher;
  private readonly urlFetcher: UrlFetcher;
  private readonly hints: readonly string[];

  constructor(opts: MirrorFetcherOptions) {
    this.pds = opts.pds;
    this.urlFetcher = opts.urlFetcher;
    this.hints = opts.hints;
  }

  async getBlob(did: string, cid: string): Promise<Uint8Array> {
    const urls = candidateUrls(this.hints, cid);
    if (urls.length === 0) return this.pds.getBlob(did, cid);

    // Race every candidate in parallel; a candidate's promise resolves ONLY on a hash-verified match
    // and rejects otherwise (fetch failure or mismatch), so `Promise.any` yields the first verified
    // winner and ignores the rest. Any verified winner has bytes identical to `cid` by definition, so
    // which candidate happens to win the race is irrelevant to correctness.
    try {
      return await Promise.any(
        urls.map(async (url) => {
          const bytes = await this.urlFetcher.fetchUrl(url);
          const actual = await recomputeBlobCid(bytes);
          if (actual !== cid) throw new Error(`mirror bytes at ${url} do not hash to ${cid}`);
          return bytes;
        }),
      );
    } catch {
      // Every mirror failed or mismatched: fall back to the authoritative PDS. Do NOT swallow a PDS
      // error — let it propagate so retrieval fails closed when even the PDS can't serve authentic
      // bytes (the caller's VerifyingCache re-verifies these bytes again on read regardless).
      return this.pds.getBlob(did, cid);
    }
  }
}
